import { Column, Entity, Index } from 'typeorm';
import type {
	BookingQuestionModel,
	BookingTypeStatus,
	BookingWhereKind,
	BookingWhoCanBook,
} from '../../constants/booking';
import { BaseEntity } from './base.entity';

/**
 * PD-17646 — something a guest can book with a person: its title, length, where it
 * happens and the rules for when it can be booked. Weekly hours are in
 * `booking_availability`; the name in its link is in `booking_name`, so a rename keeps
 * the old link working.
 */
@Index('IDX_booking_type_user', ['fkUserId'])
@Entity('booking_type')
export class BookingTypeEntity extends BaseEntity {
	/** The host, a tenant user. */
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	/** The title guests see. */
	/**
	 * PD-17649 — set on a show's booth booking. The type then belongs to the show, not to
	 * `fk_user__id`: hosts come from the booth shifts and it never appears in a person's
	 * own booking pages.
	 */
	@Column('int', { name: 'fk_trade_show__id', unsigned: true, nullable: true })
	public fkTradeShowId!: number | null;

	/** PD-17649 — the tenant's choice of where the lasting booth link goes between shows. */
	@Column({ type: 'boolean', name: 'isBoothFallback', width: 1, default: () => "'0'" })
	public isBoothFallback!: boolean;

	@Column('varchar', { name: 'title', length: 160 })
	public title!: string;

	@Column('int', { name: 'lengthMinutes', unsigned: true })
	public lengthMinutes!: number;

	@Column('varchar', { name: 'whereKind', length: 16 })
	public whereKind!: BookingWhereKind;

	/** The address for in person, or a line such as "I call them" for phone. */
	@Column('varchar', { name: 'whereDetail', length: 255, nullable: true })
	public whereDetail!: string | null;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	/** Asked on top of name, company, email and mobile. */
	@Column('json', { name: 'questions', nullable: true })
	public questions!: Array<BookingQuestionModel> | null;

	/** IANA zone the weekly hours are written in. Guests see times in their own. */
	@Column('varchar', { name: 'timezone', length: 64 })
	public timezone!: string;

	/** Leave out the times the host's connected calendar shows as busy. */
	@Column({ type: 'boolean', name: 'hideBusy', width: 1, default: () => "'1'" })
	public hideBusy!: boolean;

	/** Shortest notice before a meeting, in minutes. */
	@Column('int', { name: 'minNoticeMinutes', unsigned: true, default: 240 })
	public minNoticeMinutes!: number;

	/** How many days ahead a meeting can be booked. */
	@Column('int', { name: 'maxDaysAhead', unsigned: true, default: 30 })
	public maxDaysAhead!: number;

	/** Time kept free after each meeting, in minutes. */
	@Column('int', { name: 'gapAfterMinutes', unsigned: true, default: 0 })
	public gapAfterMinutes!: number;

	/** Most meetings of this type in a day; null for no limit. */
	@Column('int', { name: 'maxPerDay', unsigned: true, nullable: true })
	public maxPerDay!: number | null;

	/** Offer a start time every this many minutes. */
	@Column('int', { name: 'stepMinutes', unsigned: true, default: 30 })
	public stepMinutes!: number;

	@Column('varchar', { name: 'whoCanBook', length: 20, default: 'anyone' })
	public whoCanBook!: BookingWhoCanBook;

	@Column('varchar', { name: 'status', length: 12, default: 'on' })
	public status!: BookingTypeStatus;

	/** Order on the host's page and settings. */
	@Column('int', { name: 'sortOrder', unsigned: true, default: 0 })
	public sortOrder!: number;
}
